/**
 * Scroll Animations
 *
 * Handles scroll-triggered animations using IntersectionObserver:
 * - Fade-in animations for elements
 * - Video playback on scroll
 */

(function() {
  'use strict';

  /**
   * Fade-in animation observer
   * Triggers fade-in-up animation when elements come into view
   */
  function initFadeInAnimations() {
    const fadeInElements = document.querySelectorAll('[data-animate-fade-in]');

    if (fadeInElements.length === 0) return;

    const fadeInObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            fadeInObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    fadeInElements.forEach((element) => {
      fadeInObserver.observe(element);
    });
  }

  /**
   * Video playback observer
   * Plays videos when they come into view
   */
  function initVideoPlayback() {
    const videos = document.querySelectorAll('[data-scroll-video]');

    if (videos.length === 0) return;

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const video = entry.target;

            // Play video with error handling
            const playPromise = video.play();

            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  // Video playback started successfully
                  console.log('Video playback started');
                })
                .catch((error) => {
                  // Auto-play was prevented
                  console.log('Video autoplay prevented:', error);
                });
            }

            // Unobserve after playing
            videoObserver.unobserve(video);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px',
      }
    );

    videos.forEach((video) => {
      videoObserver.observe(video);
    });
  }

  /**
   * General scroll reveal animation
   * Adds 'visible' class when elements scroll into view
   */
  function initScrollReveal() {
    const revealElements = document.querySelectorAll('[data-scroll-reveal]');

    if (revealElements.length === 0) return;

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');

            // Check if element should be observed only once
            const once = entry.target.dataset.scrollReveal === 'once';
            if (once) {
              revealObserver.unobserve(entry.target);
            }
          } else {
            // Remove class if scrolling back up and not 'once'
            const once = entry.target.dataset.scrollReveal === 'once';
            if (!once) {
              entry.target.classList.remove('visible');
            }
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    revealElements.forEach((element) => {
      revealObserver.observe(element);
    });
  }

  /**
   * Initialize all scroll animations
   */
  function init() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      console.warn('IntersectionObserver not supported. Animations disabled.');
      return;
    }

    initFadeInAnimations();
    initVideoPlayback();
    initScrollReveal();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
